import { type ReactNode, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLogout } from '../auth/session';
import { Brand } from './Logo';
import ui from '../styles/ui.module.css';
import styles from './DesktopOnly.module.css';

/** Narrower than a tablet: a phone, upright or turned. */
const PHONE = '(max-width: 767px)';
const SHOW_ANYWAY = 'corocanto.phoneShowAnyway';

function subscribe(onChange: () => void): () => void {
  const query = window.matchMedia(PHONE);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function usePhoneScreen(): boolean {
  return useSyncExternalStore(subscribe, () => window.matchMedia(PHONE).matches);
}

function shownAnyway(): boolean {
  try {
    return sessionStorage.getItem(SHOW_ANYWAY) === '1';
  } catch {
    return false;
  }
}

/**
 * The signed-in pages are made for a computer (at least 1280 px wide). On a phone they show a
 * notice instead, with "Show the app anyway" (for this tab) and "Log out". Signing in, joining and
 * the setup links fit any screen, since invitation links are often opened on a phone.
 */
export function DesktopOnly({ children }: { children: ReactNode }) {
  const phone = usePhoneScreen();
  const [anyway, setAnyway] = useState(shownAnyway);
  if (!phone || anyway) {
    return <div className={styles.desktop}>{children}</div>;
  }
  return (
    <PhoneNotice
      onShowAnyway={() => {
        try {
          sessionStorage.setItem(SHOW_ANYWAY, '1');
        } catch {
          // Private browsing: it only lasts until the page reloads.
        }
        setAnyway(true);
      }}
    />
  );
}

function PhoneNotice({ onShowAnyway }: { onShowAnyway: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  return (
    <main className={styles.wrap}>
      <div className={`${ui.card} ${styles.card}`} data-testid="phone-notice">
        <Brand size={40} className={styles.brand} />
        <h1 className={styles.title}>{t('phone.title')}</h1>
        <p>{t('phone.body', { address: window.location.host })}</p>
        <p className={ui.muted}>{t('phone.later')}</p>
        <div className={styles.actions}>
          <button type="button" className={ui.button} onClick={onShowAnyway}>
            {t('phone.showAnyway')}
          </button>
          <button
            type="button"
            className={ui.button}
            disabled={logout.isPending}
            onClick={() => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })}
          >
            {t('nav.logout')}
          </button>
        </div>
      </div>
    </main>
  );
}
